import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, Send, Loader2, Sparkles, Minimize2, Maximize2, BookOpen } from "lucide-react";

export const AIChat = ({ isOpen, onClose, vertical = "support" }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [suggestedArticles, setSuggestedArticles] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);
    setSuggestedArticles([]);

    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: userMessage,
        vertical: vertical,
        conversation_id: conversationId
      });
      setConversationId(res.data.conversation_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
      if (res.data.suggested_articles?.length > 0) {
        setSuggestedArticles(res.data.suggested_articles);
      }
    } catch (error) {
      setMessages(prev => [...prev, { role: "assistant", content: "I apologize, but I encountered an error. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  if (minimized) {
    return (
      <div className="fixed bottom-6 right-6 z-50" data-testid="ai-chat-minimized">
        <button
          onClick={() => setMinimized(false)}
          className="flex items-center gap-3 px-4 py-3 rounded-sm bg-[#0046AD] text-white shadow-lg hover:bg-[#003790] transition-colors"
        >
          <Sparkles className="w-5 h-5" />
          <span className="font-semibold">AI Assistant</span>
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="ai-chat-panel" data-testid="ai-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-[#0046AD]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-white/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-display font-bold text-white">AI ASSISTANT</h3>
            {/* <p className="text-xs text-blue-200">Powered by GPT-5.2</p> */}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="text-white/80 hover:text-white hover:bg-white/20" onClick={() => setMinimized(true)} data-testid="chat-minimize">
            <Minimize2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="text-white/80 hover:text-white hover:bg-white/20" onClick={onClose} data-testid="chat-close">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4 bg-white" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="text-center py-8 bg-white" data-testid="chat-empty">
            <div className="w-16 h-16 rounded-sm bg-[#0046AD]/10 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-[#0046AD]" />
            </div>
            <h4 className="font-display font-bold text-foreground mb-2">HALCYONAUTO AI ASSISTANT</h4>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              I can help with sales, service, financing, parts, marketing, and customer support questions.
            </p>
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {["Vehicle pricing", "Service scheduling", "Financing options", "Parts lookup"].map((q) => (
                <button
                  key={q}
                  onClick={() => setInput(q)}
                  className="text-xs px-3 py-1.5 rounded-sm border border-border hover:border-[#0046AD] hover:text-[#0046AD] transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          {messages.map((msg, idx) => (
            <div key={idx} className={msg.role === "user" ? "chat-bubble-user" : "chat-bubble-assistant"} data-testid={`chat-msg-${idx}`}>
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
          ))}
          {loading && (
            <div className="chat-bubble-assistant" data-testid="chat-loading">
              <div className="typing-indicator"><span></span><span></span><span></span></div>
            </div>
          )}
        </div>

        {suggestedArticles.length > 0 && (
          <div className="mt-4 p-3 bg-muted rounded-sm" data-testid="suggested-articles">
            <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
              <BookOpen className="w-3 h-3" /> RELATED ARTICLES
            </p>
            {suggestedArticles.map((article, idx) => (
              <a key={idx} href="#" className="block text-sm text-[#0046AD] hover:underline py-1">{article.title}</a>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t border-border bg-card">
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask anything about HalcyonAuto..."
            className="input-allplan flex-1"
            disabled={loading}
            data-testid="chat-input"
          />
          <Button onClick={sendMessage} disabled={!input.trim() || loading} className="btn-allplan px-3" data-testid="chat-send">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIChat;
